<template>
  <print v-if="isPrint" :form="form" :board="board" />
  <div v-else>
    <!--    기본정보-->
    <field-select-module v-model="form.stdInfo" :field-name="$t('회사명(필수)')" :board="this.board" :is-print="isPrint" />

    <!--    위험성평가 정보-->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i>{{ $t("위험성평가 정보") }}</h5>
      <div class="writeGridContainer">
        <div>
          <div>{{ $t("공종명(필수)") }}</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_2 }}</p>
          <ml-input v-else v-model="form.inputB_2" :readonly="board.readonly" />
        </div>
        <div>
          <div>{{ $t("작업내용") }}</div>
          <p v-if="isPrint" class="print-input">{{ form.작업내용 }}</p>
          <ml-input v-else v-model="form.작업내용" :readonly="board.readonly" />
        </div>
        <div>
          <div>{{ $t("평가일(필수)") }}</div>
          <p v-if="isPrint" class="print-input">{{ form.inputB_4 }}</p>
          <date-selector v-else v-model="form.inputB_4" :readonly="board.readonly" />
        </div>
        <div>
          <div>{{ $t("예정공정표 첨부") }}</div>
          <p v-if="isPrint" class="print-input">
            {{ form.inputB_10.fileName }}
          </p>
          <file-uploader :readonly="board.readonly" v-else v-model="form.inputB_10" />
        </div>
      </div>
    </div>

    <!--    위험성 평가표-->
    <table-risk-appraisal-ep ref="table-risk-appraisal-ep" class="mt-5" :title="$t('위험성평가표')" :value="form.riskAppraisalList" @input="updateList" :isPrint="isPrint" :readonly="board.readonly" :hide-code-number="true" />

    <!-- 안전·보건관리자 검토의견 -->
    <table-risk-appraisal-ep-2 class="mt-5" :title="$t('안전·보건관리자 검토의견')" v-model="form.riskAppraisalList2" :isPrint="isPrint" :readonly="board.readonly" />

    <!-- 결재란 -->
    <div class="mt-8">
      <div>
        <h5><i class="dot"></i>{{ $t('결재란') }}</h5>
      </div>
      <name-with-signature-row :label-name="$t('작성자')" :name.sync="form.sign1.name" :signature.sync="form.sign1.sign" />
      <name-with-signature-row :label-name="$t('검토자')" :name.sync="form.sign2.name" :signature.sync="form.sign2.sign" />
      <name-with-signature-row :label-name="$t('회람자')" :name.sync="form.sign3.name" :signature.sync="form.sign3.sign" />
      <name-with-signature-row :label-name="$t('승인자')" :name.sync="form.sign4.name" :signature.sync="form.sign4.sign" />
    </div>

    <!--    개선보고서-->
    <photo-upload-before-after-table class="mt-4" :section-title="$t('개선현황')" v-model="form.photoListBeforeAfter" :is-print="isPrint" :readonly="board.readonly" @input="inputPhotoList" />

    <!--    파일첨부-->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import DateSelector from "@/components/selector/DateSelector";
import FileUploader from "@/components/FileUploader";
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import TableRiskAppraisalEp from "@/components/TableRiskAppraisalEp.vue";
import TableRiskAppraisalEp2 from "@/components/TableRiskAppraisalEp2.vue";
import PhotoUploadBeforeAfterTable from "@/views/modules/PhotoUploadBeforeAfterTable.vue";
import { required } from "vuelidate/lib/validators";
import Print from "./Print.vue";
import NameWithSignatureRow from "@/components/NameWithSignatureRow.vue";

export default {
  components: {
    NameWithSignatureRow,
    Print,
    TableRiskAppraisalEp,
    TableRiskAppraisalEp2,
    FileUploadModule,
    FileUploader,
    DateSelector,
    FieldSelectModule,
    PhotoUploadBeforeAfterTable,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  validations: {
    form: {
      stdInfo: {
        field_name: { required },
      },
      inputB_2: { required },
      inputB_4: { required },
    },
  },
  watch: {
    value: {
      important: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (this.value[key]) {
            this.form[key] = this.value[key];

            if (
              key === "photoListBeforeAfter" &&
              this.form.photoList.length > 0 &&
              this.form.photoList[0].id &&
              this.form.photoListAfter.length > 0 &&
              this.form.photoListAfter[0].id &&
              this.value[key] &&
              this.value[key][0] &&
              this.value[key][0].이전사진 &&
              this.value[key][0].이후사진 &&
              !this.value[key][0].이전사진.id &&
              !this.value[key][0].이후사진.id
            ) {
              const beforeLen = this.form.photoList.length;
              const afterLen = this.form.photoListAfter.length;

              const target = beforeLen >= afterLen ? this.form.photoList : this.form.photoListAfter;

              const setData = target.map((item, index) => {
                return {
                  id: this.generateUniqueId(),
                  이전사진: this.form.photoList[index],
                  이후사진: this.form.photoListAfter[index],
                };
              });

              this.form[key] = setData;
            }
          }
        }
      },
    },
    form: {
      important: false,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (this.value[key]) {
            this.form[key] = this.value[key];
          }
        }

        this.form.summary = {
          정기위험성평가_사업장명: [],
          정기위험성평가_재해형태: [],
        };

        this.form.summary["정기위험성평가_사업장명"].push({
          [this.form.stdInfo.field_name]: 1,
        });

        for (const data of this.form.riskAppraisalList) {
          this.form.summary["정기위험성평가_재해형태"].push({
            [data.input_3]: 1,
          });
        }

        this.$emit("input", this.form);
      },
    },
  },
  data() {
    return {
      inspectionTitle: "방파제",

      form: {
        stdInfo: {},

        inputB_1: "",
        inputB_2: "",
        inputB_3: "",
        inputB_4: "",
        inputB_5: "",
        inputB_6: "",
        inputB_7: "",
        inputB_8: "",
        inputB_9: "",
        inputB_10: {}, // file
        작업내용: "",

        photoList: [],
        photoListAfter: [],
        photoListBeforeAfter: [],

        sign1: {
          name: "",
          sign: "",
        },
        sign2: {
          name: "",
          sign: "",
        },
        sign3: {
          name: "",
          sign: "",
        },
        sign4: {
          name: "",
          sign: "",
        },

        fileList: [],
        riskAppraisalList: [],
        riskAppraisalList2: [],
        disasters: "",
      },
    };
  },
  methods: {
    generateUniqueId() {
      return "_" + Math.random().toString(36).substr(2, 9);
    },
    inputPhotoList(list) {
      this.form.photoList = list.map((item) => item.이전사진);
      this.form.photoListAfter = list.map((item) => item.이후사진);
    },
    translationMap: function () {
      const prefix = "위험성평가.정기 위험성평가(5x4)";

      const translateData = {
        [`${prefix}.기본정보.회사명`]: this.form.stdInfo.field_name,
        [`${prefix}.기본정보.작성자이름`]: this.form.stdInfo.writer_name,
        [`${prefix}.기본정보.작성일`]: this.form.stdInfo.date,
        [`${prefix}.위험성평가정보.공정명`]: this.form.inputB_2,
        [`${prefix}.위험성평가정보.작업내용`]: this.form.작업내용,
        [`${prefix}.위험성평가정보.평가일`]: this.form.inputB_4,
        [`${prefix}.위험성평가표`]: this.form.riskAppraisalList.map((item) => {
          return {
            세부작업명: item.input_1,
            평가구분: item.input_2,
            유해위험요인: item.input_4,
            재해형태: item.input_3,
            현재안전보건조치: item.input_5,
            "현재위험도.가능성": item.input_6,
            "현재위험도.중대성": item.input_7,
            "현재위험도.위험도": this.$refs["table-risk-appraisal-ep"].getResultRisk(item.input_8),
            위험감소대책: item.input_9,
            "개선후위험도.가능성": item.input_11,
            "개선후위험도.중대성": item.input_12,
            "개선후위험도.위험도": this.$refs["table-risk-appraisal-ep"].getResultRisk(item.input_13),
          };
        }),

        [`${prefix}.안전보건관리자 검토의견`]: this.form.riskAppraisalList2.map((item) => {
          return {
            세부작업명: item.input1,
            검토의견: item.input2,
            "검토의견 반영 위험도.가능성": item.input3,
            "검토의견 반영 위험도.중대성": item.input4,
            "검토의견 반영 위험도.위험도": item.input5,
            검토자: item.input6,
            비고: item.input7,
          };
        }),

        [`${prefix}.조치 전 사진`]: this.form.photoList.map((item) => {
          return { 사진설명: item.input };
        }),
        [`${prefix}.조치 후 사진`]: this.form.photoListAfter.map((item) => {
          return { 사진설명: item.input };
        }),
      };

      return translateData;
    },
    translate(translate) {
      const prefix = "위험성평가.정기 위험성평가(5x4)";

      this.form.stdInfo.field_name = translate[`${prefix}.기본정보.회사명`];
      this.form.stdInfo.writer_name = translate[`${prefix}.기본정보.작성자이름`];
      this.form.stdInfo.date = translate[`${prefix}.기본정보.작성일`];
      this.form.inputB_2 = translate[`${prefix}.위험성평가정보.공정명`];
      this.form.작업내용 = translate[`${prefix}.위험성평가정보.작업내용`];
      this.form.inputB_4 = translate[`${prefix}.위험성평가정보.평가일`];

      const riskAppraisalList = translate[`${prefix}.위험성평가표`];
      const _riskAppraisalList = JSON.parse(JSON.stringify(this.form.riskAppraisalList));
      for (let i = 0; i < riskAppraisalList.length; i++) {
        _riskAppraisalList[i].input_1 = riskAppraisalList[i]["세부작업명"];
        _riskAppraisalList[i].input_2 = riskAppraisalList[i]["평가구분"];
        _riskAppraisalList[i].input_3 = riskAppraisalList[i]["재해형태"];
        _riskAppraisalList[i].input_4 = riskAppraisalList[i]["유해위험요인"];
        _riskAppraisalList[i].input_5 = riskAppraisalList[i]["현재안전보건조치"];
        _riskAppraisalList[i].input_6 = riskAppraisalList[i]["현재위험도.가능성"];
        _riskAppraisalList[i].input_7 = riskAppraisalList[i]["현재위험도.중대성"];
        _riskAppraisalList[i].input_8 = riskAppraisalList[i]["현재위험도.위험도"];
        _riskAppraisalList[i].input_9 = riskAppraisalList[i]["위험감소대책"];
        _riskAppraisalList[i].input_11 = riskAppraisalList[i]["개선후위험도.가능성"];
        _riskAppraisalList[i].input_12 = riskAppraisalList[i]["개선후위험도.중대성"];
        _riskAppraisalList[i].input_13 = riskAppraisalList[i]["개선후위험도.위험도"];
      }
      this.$set(this.form, "riskAppraisalList", _riskAppraisalList);

      const riskAppraisalList2 = translate[`${prefix}.안전보건관리자 검토의견`];
      const _riskAppraisalList2 = JSON.parse(JSON.stringify(this.form.riskAppraisalList2));
      for (let i = 0; i < riskAppraisalList2.length; i++) {
        _riskAppraisalList2[i].input1 = riskAppraisalList2[i]["세부작업명"];
        _riskAppraisalList2[i].input2 = riskAppraisalList2[i]["검토의견"];
        _riskAppraisalList2[i].input3 = riskAppraisalList2[i]["검토의견 반영 위험도.가능성"];
        _riskAppraisalList2[i].input4 = riskAppraisalList2[i]["검토의견 반영 위험도.중대성"];
        _riskAppraisalList2[i].input5 = riskAppraisalList2[i]["검토의견 반영 위험도.위험도"];
        _riskAppraisalList2[i].input6 = riskAppraisalList2[i]["검토자"];
        _riskAppraisalList2[i].input7 = riskAppraisalList2[i]["비고"];
      }
      this.$set(this.form, "riskAppraisalList2", _riskAppraisalList2);

      const photoList = translate[`${prefix}.조치 전 사진`];
      const _photoList = JSON.parse(JSON.stringify(this.form.photoList));
      for (let i = 0; i < photoList.length; i++) {
        _photoList[i].input = photoList[i]["사진설명"];
      }
      this.$set(this.form, "photoList", _photoList);

      const photoListAfter = translate[`${prefix}.조치 후 사진`];
      const _photoListAfter = JSON.parse(JSON.stringify(this.form.photoListAfter));
      for (let i = 0; i < _photoListAfter.length; i++) {
        _photoListAfter[i].input = photoListAfter[i]["사진설명"];
      }
      this.$set(this.form, "photoListAfter", _photoListAfter);
    },
    updateList(list) {
      this.form.riskAppraisalList = list;

      this.form.disasters = "";
      for (const risk of this.form.riskAppraisalList) {
        this.form.disasters += `${risk.input_3} `;
      }
    },
    checkDate(input) {
      if (Date.parse(this.form.inputB_8) > Date.parse(this.form.inputB_9)) {
        if (input === this.form.inputB_8) {
          this.form.inputB_9 = this.form.inputB_8;
        } else {
          this.form.inputB_8 = this.form.inputB_9;
        }
      }
    },

    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        if (!this.$v.form.stdInfo.field_name.required) return "회사명을 반드시 입력해야 합니다";
        else if (!this.$v.form.inputB_2.required) return "공정명을 반드시 입력해야 합니다";
        else if (!this.$v.form.inputB_4.required) return "평가일을 반드시 입력해야 합니다";
      }
    },
  },
};
</script>

<style scoped lang="scss">
$border: 1px solid #cdcdcd;
.label-text {
  width: 114px;
  font-weight: 500;
  font-size: 14px;
}
.mt-component-form {
  margin-top: 15px;
}

/* uploaded File */
.h-upload {
  height: 470px;
}
.upload-content {
  border: $border;
  &:last-child {
    border-left: none;
  }
  &__title {
    height: 54px;
    line-height: 54px;
    text-align: center;
    background: #fafafa;
    border-bottom: $border;
  }
  &__input {
    height: 54px;
    background: #f9f9f9;
    border-top: $border;
    padding: 5px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__file {
    height: calc(100% - 54px);
    width: 100%;
    padding: 14px;
  }
}
</style>
